/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {RegistryContexts} from "@app/models/constants/registry-constants";
// eslint-disable-next-line max-len
import {
  QUERY_PARAM_APP_PRESCR_ENDED,
  QUERY_PARAM_APP_PRESCR_ENDED_END_DATE,
  QUERY_PARAM_APP_PRESCR_ENDED_START_DATE,
  QUERY_PARAM_REDIRECT_TO,
} from "@app/models/constants/routing-constants";
import {CommonState} from "@app/store/common/common.state";
import {
  TP_SECTIONS
} from "@doctor-panel-modules/patient-treatment-plan/models/tp-sections-info";
import {TpService} from "@doctor-panel-modules/patient-treatment-plan/services/tp.service";
import {Select, Store} from "@ngxs/store";
import {LateralIndexMenuConf} from "@shared/shared-layout/models/lateral-index-menu-conf";
import {ContestoApp} from "@trec/api";
import {RangeDates} from "@trec/api/common";
import {TrecError} from "@trec/api/errors";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {combineLatest, Observable, Subscription} from "rxjs";
import {filter, first, map} from "rxjs/operators";
import {AppPrescriptionActions} from "../../../app-prescription/store/app-prescription.actions";
import {TP_DATA_RESOLVER_TP_CONFIG} from "../../models/treatment-plan-constants";

@Component({
  selector: "c3-treatment-plan-page",
  templateUrl: "./treatment-plan.page.html",
  styleUrls: ["./treatment-plan.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreatmentPlanPage implements OnInit, OnDestroy {
  @Select(CommonState.appPrescriptionDates) appPrescriptionDates$: Observable<
    RangeDates | null | undefined
  >;
  @Select(CommonState.patientContext) patientContext$: Observable<
    RegistryContexts | null | undefined
  >;


  appPrescriptionDates: RangeDates | null | undefined;

  menuConfs: LateralIndexMenuConf = TP_SECTIONS;

  appPrescrEnded = false;

  context: RegistryContexts | null;

  private subscr = new Subscription();

  constructor(
    private store: Store,
    private cd: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private tpService: TpService,
    private c3Snackbar: C3SnackbarStatusService,
  ) { }

  ngOnInit() {
    // Get user entry of the patient
    this.subscr.add(
      this.appPrescriptionDates$.subscribe((dates) => {
        this.appPrescriptionDates = dates;
        this.cd.markForCheck();
      }),
    );

    // Check if page should be in mode "app prescr ending"
    this.appPrescrEnded =
      this.route.snapshot.queryParamMap.get(QUERY_PARAM_APP_PRESCR_ENDED) === "true";

    switch (this.route.snapshot.queryParamMap.get(QUERY_PARAM_REDIRECT_TO)) {
      case "monitoring":
        this.router.navigate([{outlets: {"treatment-plan": "monitoring"}}], {
          relativeTo: this.route,
        });
        break;

      default:
        break;
    }

    this.patientContext$.subscribe((context) => {
      this.context = context || null;
    });

    this.subscr.add(
      combineLatest([
        this.route.data.pipe(
          map(routeData => routeData?.[TP_DATA_RESOLVER_TP_CONFIG])
        ),
        this.store.select(CommonState.currentStatoInattivo)
      ])
        .subscribe(([tpConfig, patientIsDisabled]) => {
          if (tpConfig) {
            const {monitoring, ...rest} = tpConfig;
            if (
              this.context?.includes("diabetes1") ||
              this.context?.includes("diabetes2") ||
              this.context?.includes("gestational") ||
              this.context?.includes("pneumo") ||
              this.context?.includes("parki") ||
              this.context?.includes("oncology")
            ) {
              const {
                items: {reports, ...itemsRest},
                ...monitoringRest
              } = monitoring;

              if (patientIsDisabled) {
                this.menuConfs = {
                  ...rest,
                };
              } else {
                this.menuConfs = {
                  ...rest,
                  monitoring: {
                    items: itemsRest,
                    ...monitoringRest,
                  },
                };
              }
            } else {
              this.menuConfs = tpConfig;

              if (patientIsDisabled) {
                this.menuConfs = {
                  ...rest,
                };
              } else {
                this.menuConfs = tpConfig
              }
            }
            if (patientIsDisabled) {
              this.tpService.registerCurrentTpSections({...rest});
            } else {
              this.tpService.registerCurrentTpSections(tpConfig);
            }
            this.cd.markForCheck();

            // Redirect if section is missing (see method docs for more info)
            this.readirectIfTpSectionMissing();
          }
        }),
    );

    // Listen for events navigation -> if user navigate between the treatment plan section, be sure that it can enter in the requested
    // section, otherwise, redirect he/she to the first available section.
    this.subscr.add(
      this.router.events.pipe(filter((ev) => ev instanceof NavigationEnd)).subscribe(() => {
        // Redirect if section is missing (see method docs for more info)
        this.readirectIfTpSectionMissing();
      }),
    );
  }

  goToAppPrescription() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.router.navigate([{outlets: {"patient-page": "app-prescription"}}], {
      relativeTo: this.route.parent!.parent,
    });
  }

  onEndAppPrescr() {
    const appPrescrStartDate = parseInt(
      this.route.snapshot.queryParamMap.get(QUERY_PARAM_APP_PRESCR_ENDED_START_DATE) || "0",
      10,
    );
    const appPrescrEndDate = parseInt(
      this.route.snapshot.queryParamMap.get(QUERY_PARAM_APP_PRESCR_ENDED_END_DATE) || "0",
      10,
    );

    const patient = this.store.selectSnapshot(CommonState.patient);

    if (!patient) {
      throw new Error(`Try to unprescribe app but patient id has been not set`);
    }

    const contesti = this.store.selectSnapshot(CommonState.patientContext);
    const app = CommonState.selectCurrentApp(contesti);

    const contestoApp: ContestoApp = {
      appName: app ? app.id : "",
      appPrescr: true,
      contexts: contesti ? contesti : [],
      telemonitoring: false,
      trecUserId: patient?.trecUserId,
    };

    // Prescribe app
    this.store
      .dispatch(
        new AppPrescriptionActions.PrescribeApp({
          dates: {start: new Date(appPrescrStartDate), end: new Date(appPrescrEndDate)},
          contestoApp,
        }),
      )
      .subscribe(
        () => {
          // Open success/activation code dialog
          const patientFiscalCode = this.store.selectSnapshot(
            CommonState.patientPersonalData,
          )?.fiscalCode;
          if (patientFiscalCode) {
            // eslint-disable-next-line max-len
            // this.dialog.open(AppActivationCodeDialogComponent, {data: {patientFiscalCode, firstTime: true} as AppActivationCodeDialogData})
            //   .afterOpened().subscribe(() => {

            // Remove message and nav to patient list
            this.appPrescrEnded = false;
            this.router.navigate(["/", "doctor-panel"], {
              queryParams: {
                [QUERY_PARAM_APP_PRESCR_ENDED]: undefined,
                [QUERY_PARAM_APP_PRESCR_ENDED_START_DATE]: undefined,
                [QUERY_PARAM_APP_PRESCR_ENDED_END_DATE]: undefined,
              },
              queryParamsHandling: "merge",
            });

            this.cd.markForCheck();
            // });
          }
        },
        () => {
          this.c3Snackbar.showError({
            error: new TrecError("faulty-prescription", ""),
            messagesMap: {
              "faulty-prescription": `Si è verificato un errore durante l'attivazione dell'app`,
            },
          });
        },
      );
  }

  /**
   * If user landed on a section which is not available in the current treatment plan configuration, redirect he/she to the first available
   * one.
   *
   * TODO this part should be done from the TpConfigResolver (not from a guard because guards are called before the resolvers by design, and
   * our guard requires the data calculated by the resolver) but for some reason router.navigate does not work even passing the current
   * activated route.
   */
  private readirectIfTpSectionMissing() {
    // Search for current treatment plan section defined in the route
    const tpRoute = this.route.children.find((c) => c.outlet === "treatment-plan");

    if (tpRoute) {
      tpRoute.url
        .pipe(
          filter((u) => u?.length > 0),
          first(),
        )
        .subscribe((url) => {
          const currentSectionId = url[0].path;

          if (this.menuConfs) {
            if (!this.menuConfs[currentSectionId]) {
              // Section not present -> redirect to first available section
              // Get first section
              const nextSectionId = Object.keys(this.menuConfs)[0];
              // Navigate
              if (nextSectionId) {
                this.router.navigate([{outlets: {"treatment-plan": nextSectionId}}], {
                  relativeTo: this.route,
                  queryParamsHandling: "preserve",
                });
              }
            }
          }
        });
    }
  }

  ngOnDestroy() {
    this.subscr.unsubscribe();
  }
}
